// src/pages/CategoriesPage.jsx
import React, { useState, useMemo } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  Breadcrumbs,
  Link,
  Fade,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Grid,
  Chip,
  ButtonGroup,
  Dialog
} from '@mui/material';
import {
  ViewList as ViewListIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  GridView as GridViewIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useCategories } from '../context/CategoryContext';
import CategoryList from '../components/categories/CategoryList';
import CategoryForm from '../components/categories/CategoryForm';
import CategoryGrid from '../components/categories/CategoryGrid';
import Loader from '../components/common/Loader';
import { useCategoryName } from '../utils/categoryTranslation';

const normalizeCategorySearchText = (value) => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim();

const CategoriesPage = () => {
  const { loading, categories, categoriesList, createCategory } = useCategories();
  const { t, i18n } = useTranslation('common');
  const { user } = useAuth();
  const isAdmin = Boolean(user?.isAdmin);
  const isOwner = Boolean(user?.isOwner);
  const isAuthenticated = Boolean(user);
  const getCategoryName = useCategoryName();
  const [tabValue, setTabValue] = useState(0);
  const [openForm, setOpenForm] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);

  const handleOpenForm = (category = null) => {
    setSelectedCategory(category);
    setOpenForm(true);
  };

  const handleCloseForm = () => {
    setOpenForm(false);
    setSelectedCategory(null);
  };

  const handleSubmitForm = async (data) => {
    if (isOwner && !isAdmin) {
      data.pendingApproval = true;
      data.isActive = false;
    }
    await createCategory(data);
    handleCloseForm();
  };
  const [viewMode, setViewMode] = useState('grid'); // Défaut en mode grille
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [filterBy, setFilterBy] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const normalizedSearchQuery = normalizeCategorySearchText(searchQuery);
  const isSearching = normalizedSearchQuery.length > 0;

  // Options de tri
  const sortOptions = [
    { value: 'name', label: t('categories.sorting.nameAsc') },
    { value: 'name_desc', label: t('categories.sorting.nameDesc') },
    { value: 'children_count', label: t('categories.sorting.childrenCount') },
    { value: 'created_desc', label: t('categories.sorting.createdDesc') },
    { value: 'created_asc', label: t('categories.sorting.createdAsc') }
  ];

  // Options de filtrage
  const filterOptions = [
    { value: 'all', label: t('categories.filtering.all') },
    { value: 'parent', label: t('categories.filtering.parent') },
    { value: 'with_children', label: t('categories.filtering.withChildren') },
    { value: 'with_image', label: t('categories.filtering.withImage') }
  ];

  // Catégories filtrées et triées
  const filteredAndSortedCategories = useMemo(() => {
    const rootCategories = Array.isArray(categories)
      ? categories.filter(cat => !cat.parentId)
      : [];
    const allLoadedCategories = Array.isArray(categoriesList) && categoriesList.length > 0
      ? [...categoriesList]
      : [...rootCategories];

    let filtered = isSearching ? allLoadedCategories : rootCategories;
    const effectiveFilter = !isSearching && filterBy === 'all' ? 'parent' : filterBy;

    // Filtrage
    switch (effectiveFilter) {
      case 'parent':
        filtered = filtered.filter(cat => !cat.parentId);
        break;
      case 'with_children':
        filtered = filtered.filter(cat => cat.children && cat.children.length > 0);
        break;
      case 'with_image':
        filtered = filtered.filter(cat => cat.imageUrl);
        break;
      default:
        break;
    }

    if (isSearching) {
      filtered = filtered.filter((category) => {
        const searchableText = [
          getCategoryName(category),
          category.description,
          category.fullPath,
          category.slug
        ]
          .map(normalizeCategorySearchText)
          .filter(Boolean)
          .join(' ');

        return searchableText.includes(normalizedSearchQuery);
      });
    }

    // Tri
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return getCategoryName(a).localeCompare(getCategoryName(b));
        case 'name_desc':
          return getCategoryName(b).localeCompare(getCategoryName(a));
        case 'children_count':
          return (b.children?.length || 0) - (a.children?.length || 0);
        case 'created_desc':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'created_asc':
          return new Date(a.createdAt) - new Date(b.createdAt);
        default:
          return 0;
      }
    });

    return filtered;
  }, [categories, categoriesList, filterBy, sortBy, getCategoryName, i18n.language, isSearching, normalizedSearchQuery]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setSearchQuery('');
  };

  const handleViewModeChange = (mode) => {
    setViewMode(mode);
  };

  const handleSearchChange = (event) => {
    const query = event.target.value;
    setSearchQuery(query);
  };

  const handleClearFilters = () => {
    setSortBy('name');
    setFilterBy('all');
    setSearchQuery('');
  };

  const activeFiltersCount = [
    sortBy !== 'name' ? 1 : 0,
    filterBy !== 'all' ? 1 : 0
  ].reduce((a, b) => a + b, 0);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* En-tête avec breadcrumbs */}
        <Box sx={{ mb: 4 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link component={RouterLink} to="/" color="inherit">
              {t('navigation.home')}
            </Link>
            <Typography color="text.primary">{t('navigation.categories')}</Typography>
          </Breadcrumbs>
          
          <Box sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'flex-start', sm: 'center' },
            justifyContent: 'space-between',
            gap: 2,
            py: 2,
            px: 3,
            borderRadius: 2,
            background: 'linear-gradient(45deg, #6200ee 30%, #03dac4 90%)',
            color: 'white',
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }}>
            <Box>
              <Typography
                variant="h4"
                component="h1"
                sx={{ fontWeight: 700, mb: 0.5, color: 'white' }}
              >
                {t('categories.title')}
              </Typography>
              <Typography variant="subtitle1" sx={{ color: 'white' }}>
                {t('categories.subtitle')}
              </Typography>
            </Box>
            {isAuthenticated && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => handleOpenForm()}
                sx={{
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  boxShadow: 'none',
                  flexShrink: 0
                }}
              >
                {isOwner && !isAdmin
                  ? t('categoryManagement.proposeCategory', 'Proposer une catégorie')
                  : t('categoryManagement.newCategory', 'Nouvelle catégorie')}
              </Button>
            )}
          </Box>
        </Box>

        {/* Filtres intégrés */}
        <Paper 
          elevation={3}
          sx={{ 
            p: 2, 
            mb: 3,
            borderRadius: 3,
            background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
          }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder={t('categories.searchPlaceholder')}
                value={searchQuery}
                onChange={handleSearchChange}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                  sx: { 
                    borderRadius: 2,
                    backgroundColor: 'background.paper'
                  }
                }}
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}>
                <Button
                  variant={showFilters ? "contained" : "outlined"}
                  onClick={() => setShowFilters(!showFilters)}
                  startIcon={<FilterIcon />}
                  size="small"
                  sx={{ minWidth: 120 }}
                >
                  {t('categories.filters')} {activeFiltersCount > 0 && `(${activeFiltersCount})`}
                </Button>
                
                {activeFiltersCount > 0 && (
                  <Button
                    variant="outlined"
                    onClick={handleClearFilters}
                    startIcon={<ClearIcon />}
                    size="small"
                    color="secondary"
                  >
                    {t('categories.clearFilters')}
                  </Button>
                )}
              </Box>
            </Grid>

            {tabValue === 0 && (
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <ButtonGroup size="small" variant="outlined">
                    <Button
                      variant={viewMode === 'list' ? 'contained' : 'outlined'}
                      onClick={() => handleViewModeChange('list')}
                      startIcon={<ViewListIcon />}
                    >
                      {t('categories.viewModes.list')}
                    </Button>
                    <Button
                      variant={viewMode === 'grid' ? 'contained' : 'outlined'}
                      onClick={() => handleViewModeChange('grid')}
                      startIcon={<GridViewIcon />}
                    >
                      {t('categories.viewModes.grid')}
                    </Button>
                  </ButtonGroup>
                </Box>
              </Grid>
            )}
          </Grid>

          {/* Panneau de filtres dépliable */}
          {showFilters && (
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('categories.sorting.label')}</InputLabel>
                    <Select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value)}
                      label={t('categories.sorting.label')}
                    >
                      {sortOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('categories.filtering.label')}</InputLabel>
                    <Select
                      value={filterBy}
                      onChange={(e) => setFilterBy(e.target.value)}
                      label={t('categories.filtering.label')}
                    >
                      {filterOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* Chips des filtres actifs */}
          {activeFiltersCount > 0 && (
            <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  {t('categories.activeFilters')}:
                </Typography>
                
                {searchQuery && (
                  <Chip
                    label={`${t('categories.search')}: "${searchQuery}"`}
                    onDelete={() => setSearchQuery('')}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                )}
                
                {sortBy !== 'name' && (
                  <Chip
                    label={`${t('categories.sorting.label')}: ${sortOptions.find(o => o.value === sortBy)?.label}`}
                    onDelete={() => setSortBy('name')}
                    color="secondary"
                    variant="outlined"
                    size="small"
                  />
                )}
                
                {filterBy !== 'all' && (
                  <Chip
                    label={`${t('categories.filtering.label')}: ${filterOptions.find(o => o.value === filterBy)?.label}`}
                    onDelete={() => setFilterBy('all')}
                    color="secondary"
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            </Box>
          )}
        </Paper>

        {/* Onglets */}
        {!isSearching && (
          <Paper sx={{ mb: 3, borderRadius: 2 }}>
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                aria-label="category view tabs"
                centered
              >
                <Tab 
                  icon={<ViewListIcon />} 
                  label={t('categories.tabs.list')} 
                  id="tab-0" 
                  aria-controls="tabpanel-0" 
                />
              </Tabs>
            </Box>
          </Paper>
        )}

        {/* Contenu principal */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <Loader />
          </Box>
        ) : (
          <Fade in timeout={500}>
            <Box>
              <>
                <Box role="tabpanel" hidden={tabValue !== 0} id="tabpanel-0" aria-labelledby="tab-0">
                  {tabValue === 0 && (
                    viewMode === 'grid' ? 
                      <CategoryGrid categories={filteredAndSortedCategories} /> :
                      <CategoryList categories={filteredAndSortedCategories} />
                  )}
                </Box>
              </>
            </Box>
          </Fade>
        )}
      </motion.div>

      {isAuthenticated && (
        <Dialog open={openForm} onClose={handleCloseForm} fullWidth maxWidth="md">
          <CategoryForm
            category={selectedCategory}
            onSubmit={handleSubmitForm}
            onCancel={handleCloseForm}
            isOwner={isOwner && !isAdmin}
          />
        </Dialog>
      )}
    </Container>
  );
};

export default CategoriesPage;